import React, { useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import type { UserOption } from 'src/components/AccountsAutocomplete';
import { AccountsAutocomplete } from 'src/components/AccountsAutocomplete';
import { AlineButton, AlineTooltip, AnimatedAlineDialog } from 'src/components/generic';
import { PersonIcon } from 'src/components/icons/PersonIcon';
import type { WorkflowCollaborator } from 'src/components/workflow';
import { useCollabDialogStyles } from 'src/components/workflow';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';

interface AssignCollaboratorDialogProps {
  collaborator: WorkflowCollaborator;
  onClose: () => void;
  open: boolean;
}

export function AssignCollaboratorDialog({
  collaborator,
  onClose,
  open,
}: AssignCollaboratorDialogProps): JSX.Element {
  const { classes } = useCollabDialogStyles();
  const { orgMembers, refetch } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [userOption, setUserOption] = useState<UserOption | null>(
    collaborator.account?.__typename === 'User' ? collaborator.account : null
  );
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const dirty = userOption?.__typename === 'NewUser' || userOption?.id !== collaborator.account?.id;
  const disabledReason = !dirty ? 'Select a user to assign' : '';

  useEffect(() => {
    setUserOption(collaborator.account?.__typename === 'User' ? collaborator.account : null);
  }, [collaborator.account]);

  function resetState(): void {
    setIsSubmitting(false);
  }

  return (
    <AnimatedAlineDialog
      TransitionProps={{
        onEnter: async () => {
          await refetch();
        },
      }}
      classes={{ paper: classes.paper }}
      open={open}
      title={
        <div className={classes.title}>
          <PersonIcon />
          <Typography variant="h1">Assign Workflow Collaborator</Typography>
        </div>
      }
      onClose={onClose}
    >
      <AccountsAutocomplete
        accounts={orgMembers}
        value={userOption ? [userOption] : []}
        onChange={(_, accounts) => {
          const maybeUserOption = accounts.at(-1);
          setUserOption(maybeUserOption?.__typename !== 'Org' ? (maybeUserOption ?? null) : null);
        }}
      />
      <div className={classes.formSubmitButtons}>
        <AlineButton
          color="primary"
          disabled={isSubmitting}
          variant="outlined"
          onClick={() => {
            onClose();
            resetState();
          }}
        >
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={isSubmitting}
              variant="contained"
              onClick={async () => {
                setIsSubmitting(true);
                await changeCollaboratorAssignment({
                  variables: {
                    input: {
                      assignedUserId:
                        userOption?.__typename !== 'NewUser' ? (userOption?.id ?? null) : null,
                      email: userOption?.__typename === 'NewUser' ? userOption.email : undefined,
                      permissionId: collaborator.id,
                    },
                  },
                });
                onClose();
                resetState();
              }}
            >
              Assign
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </AnimatedAlineDialog>
  );
}
